import {asyncDownloadFile, fireConfetti, fireRequest, formToJSON, getDatasetValue, type GetDatasetValueOptions, notifyCatchedException, notifyError, openWindow, tryParseJSON} from "@/modules/utils";
import type {ResourceFileConfig, ResourceFileReadConfig, ViewIdentifiers} from "@/layouts/resources/types";
import {DocsResourcesUtils} from "@/layouts/resources/index";
import {Block} from "notiflix";

export const viewIdentifiers: ViewIdentifiers = {
    listing: '[data-view="resources"]',
    download: '#resources__download__view',
    reading: {
        wrappers: {
            inView: '#resources__inview__reading',
            standalone: '#resources__reader__standalone__view',
        },
        engine: '#reader__engine',
        main: '#resources__reader__view',
    },
}

export const toResourceFileConfig = (input: unknown, throwOnFail: boolean = false): ResourceFileConfig | null => {
    if (typeof input !== 'object' || input === null) {
        if (!throwOnFail) return null;
        else throw new Error('Invalid JSON for ResourceFileConfig');
    }

    const data = input as Record<string, string | null>;
    const options: GetDatasetValueOptions<null> = {defaultValue: null, strictDefault: true}

    try {
        return {
            id: getDatasetValue<string | null>(data, 'id', options),
            ref: getDatasetValue<string | null>(data, 'ref', options),
            size: getDatasetValue<string | null>(data, 'size', options),
            readingUrl: getDatasetValue<string | null>(data, ['readingUrl', 'read'], options),
            displayName: getDatasetValue<string | null>(data, ['displayName', 'name'], options),
            originalName: getDatasetValue<string | null>(data, ['originalName', 'o_name'], options),
        }
    } catch (e) {
        console.warn(e)
        if (!throwOnFail) return null;
        else throw new Error('Invalid JSON for ResourceFileConfig');
    }
}

export const toResourceFileReadConfig = (input: unknown, throwOnFail: boolean = false): ResourceFileReadConfig | null => {
    if (typeof input !== 'object' || input === null) {
        if (!throwOnFail) return null;
        else throw new Error('Invalid JSON for ResourceFileReadConfig');
    }

    const data = input as Record<string, string | null>;
    const options: GetDatasetValueOptions<null> = {defaultValue: null, strictDefault: true}

    try {
        return {
            id: getDatasetValue<string | null>(data, 'id', options),
            name: getDatasetValue<string | null>(data, 'name', options),
            route: getDatasetValue<string | null>(data, 'route', options),
            token: getDatasetValue<string | null>(data, ['token', '_token'], options),
        }
    } catch (e) {
        console.warn(e)
        if (!throwOnFail) return null;
        else throw new Error('Invalid JSON for ResourceFileReadConfig');
    }
}

export const getDocConfig = (scope: HTMLElement, selector: string = '[name="__config__"]'): ResourceFileConfig | null => {
    try {
        const data = tryParseJSON(scope.querySelector<HTMLInputElement>(selector)?.value);
        return toResourceFileConfig(data)
    } catch (e) {
        return null
    }
}

export const getReadConfig = (scope: HTMLElement, selector: string = '[name="__rconfig__"]'): ResourceFileReadConfig | null => {
    try {
        const data = tryParseJSON(scope.querySelector<HTMLInputElement>(selector)?.value);
        return toResourceFileReadConfig(data)
    } catch (e) {
        return null
    }
}

export const registerDownloadFormListeners = () => {
    const downloadView = document.querySelector(viewIdentifiers.download);
    if (!downloadView) return;

    const form = downloadView.querySelector<HTMLFormElement>('form');

    form.addEventListener('submit', (e) => {
        e.preventDefault();

        Block.merge({backgroundColor: 'rgba(255,255,255,0.2)'});

        const uiBlockSelector = `${viewIdentifiers.download} .modal-content`;
        const data = formToJSON(form.elements)

        fireRequest(
            form.getAttribute('data-action'),
            data, uiBlockSelector, null,
            async (response) => {
                $(downloadView).modal('hide');
                // form.reset();

                const data = response.data;

                try {
                    asyncDownloadFile(data.link, data.name).then(x => x)
                } catch (e) {
                    openWindow(data.link, {assignOnFail: true});
                } finally {
                    await fireConfetti(1500);
                }
            }
        ).catch((error) => {
            notifyCatchedException(error);
            console.log(error)
        })

        return false;
    })
}

export const renderDownloadView = (config: ResourceFileConfig, mode: 'download' | 'infos' = 'download') => {
    const downloadView = document.querySelector(viewIdentifiers.download);
    if (!downloadView || !config) {
        notifyError('Une erreur de configuration est survenue. Veuillez réessayer s\'il vous plaît!', {asFailure: true});
        return
    }

    downloadView.querySelector<HTMLInputElement>('input[name="codename"]').value = config.originalName
    downloadView.querySelector<HTMLInputElement>('input[name="hash"]').value = config.id

    downloadView.querySelector('[data-doc="name"]').textContent = config.displayName
    downloadView.querySelector('[data-doc="size"]').textContent = config.size
    downloadView.querySelector('[data-doc="ref"]').textContent = config.ref

    downloadView.setAttribute('data-rendering', mode)
    $(downloadView).modal('show')
}

export const renderReadingView = async (el: HTMLElement, inView: boolean) => {
    const config = getDocConfig(el);

    if (!config) {
        notifyError('Une erreur de configuration est survenue. Veuillez réessayer s\'il vous plaît!', {asFailure: true});
        return
    }

    // const __n = () => notifyError('Echec de lecture instantanée. Ouverture du document en cours...', {asFailure: false, mode: 'info'})

    const __open = (notify: boolean) => {
        if (notify) notifyError('Echec de lecture instantanée. Ouverture du document en cours...', {asFailure: false, mode: 'info'})
        openWindow(config.readingUrl, {assignOnFail: true});
    }

    if (inView) {
        const readViewPortal = document.querySelector<HTMLElement>(viewIdentifiers.reading.wrappers.inView);
        if (readViewPortal) {
            const view = readViewPortal.querySelector<HTMLElement>(viewIdentifiers.reading.main);

            if (!view) {
                __open(true);
                return;
            }

            try {

                view.setAttribute('data-load', 'on-demand');

                const view__rconfig__input = view.querySelector<HTMLInputElement>('[name="__rconfig__"]');
                view__rconfig__input.value = el.querySelector<HTMLInputElement>('[name="__rconfig__"]').value

                const view__dconfig__input = view.querySelector<HTMLInputElement>('[name="__dconfig__"]');
                view__dconfig__input.value = el.querySelector<HTMLInputElement>('[name="__config__"]').value


                //method 1: => get formated Configs from clicked item itself & instantiate reading view with these configs
                const readingConfig = getReadConfig(el, '[name="__rconfig__"]');
                const fileConfig = getDocConfig(el, '[name="__config__"]');

                //method 2: => get raw configs value (stringify Json values), set it to reading view configs inputs then get formated config from thhis view instead of element directly
                // const readingConfig = getReadConfig(view, '[name="__rconfig__"]');
                // const fileConfig = getDocConfig(view, '[name="__dconfig__"]');

                if (!readingConfig || !fileConfig) {
                    __open(true);
                    return;
                }

                const instance = await DocsResourcesUtils.initOnDemandReadingViewListeners({readingConfig, fileConfig, inView: true});
                const $readViewPortal = $(readViewPortal);

                $readViewPortal.on('show.bs.modal', () => {
                    instance.setTheme('light'); //force light because inview page is light mode
                    instance.clearSearch();
                    instance.toggleSidebar(window.innerWidth >= 768 ? 'on' : 'off');
                    // instance.setZoom('auto');
                    instance.goToFirst();
                })

                $readViewPortal.on('hidden.bs.modal', () => {
                    instance.resetTheme();
                    instance.destroy();
                })

                $readViewPortal.modal('show');

            } catch (e) {
                console.error(e);
                __open(true);
            }
        } else __open(true);
    } else __open(false);
}

